import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { normaliserTexte } from '../../../lib/normaliser';
import { useOfflineMutation } from '../../../hooks/useOfflineMutation';
import { useOptimisticRealtime } from '../../../hooks/useOptimisticRealtime';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import ConfirmStartConsultationModal from '../../../components/feature/ConfirmStartConsultationModal';
import PatientHistoryModal from '../../../components/feature/PatientHistoryModal';
import MedecinTour from '../../../components/feature/MedecinTour';

// ─── Helpers Calendrier ────────────────────────────────────────────
const MONTHS = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const DAY_NAMES = ['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'];

function getCalendarCells(year: number, month: number) {
  const firstDay = new Date(year, month, 1).getDay(); // 0=Dim
  const offset = firstDay === 0 ? 6 : firstDay - 1; // Lundi = 0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: { day: number; dateStr: string }[] = [];
  for (let i = 0; i < offset; i++) cells.push({ day: 0, dateStr: '' });
  for (let d = 1; d <= daysInMonth; d++) {
    const m = String(month + 1).padStart(2, '0');
    const dd = String(d).padStart(2, '0');
    cells.push({ day: d, dateStr: `${year}-${m}-${dd}` });
  }
  return cells;
}

// ─── Composant principal ───────────────────────────────────────────
export default function MedecinRendezVousPage() {
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState<any[]>([]);
  const [medecinId, setMedecinId] = useState<string | null>(null);
  const [medecinSpecialite, setMedecinSpecialite] = useState('');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [fileAttenteSalle, setFileAttenteSalle] = useState<string>('garde');
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const { mutate: offlineMutate, state: offlineState } = useOfflineMutation<any>();
  const { registerOfflineItem, clearOptimisticCache } = useOptimisticRealtime();

  const [searchQuery, setSearchQuery] = useState('');
  const [clinicPatients, setClinicPatients] = useState<any[]>([]);
  const [patientSearchTerm, setPatientSearchTerm] = useState('');
  const [patientsLoading, setPatientsLoading] = useState(false);

  // Modals existants
  const [showConfirmStartModal, setShowConfirmStartModal] = useState(false);
  const [selectedRendezVous, setSelectedRendezVous] = useState<any>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyPatientId, setHistoryPatientId] = useState('');
  const [historyPatientName, setHistoryPatientName] = useState('');

  // ─── États Calendrier (caché par défaut) ─────────────────────
  const now = new Date();
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(now.getMonth());
  const [calendarYear, setCalendarYear] = useState(now.getFullYear());
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [dayConsultations, setDayConsultations] = useState<any[]>([]);
  const [loadingDayDetail, setLoadingDayDetail] = useState(false);

  // ─── Modal Création RDV ────────────────────────────────────────
  const [showCreateRdvModal, setShowCreateRdvModal] = useState(false);
  const [createRdvDate, setCreateRdvDate] = useState('');
  const [createRdvHeureDebut, setCreateRdvHeureDebut] = useState('');
  const [createRdvHeureFin, setCreateRdvHeureFin] = useState('');
  const [createRdvMotif, setCreateRdvMotif] = useState('');
  const [createRdvType, setCreateRdvType] = useState('cabinet');
  const [createRdvDossier, setCreateRdvDossier] = useState('');
  const [createRdvPatient, setCreateRdvPatient] = useState<any>(null);
  const [createRdvSearching, setCreateRdvSearching] = useState(false);
  const [createRdvSaving, setCreateRdvSaving] = useState(false);
  const [createRdvNotFound, setCreateRdvNotFound] = useState(false);
  const createRdvSearchTimer = useRef<NodeJS.Timeout | null>(null);

  // ─── Modal Nouvelle Consultation (sans RDV) ────────────────────
  const [showNouvelleConsModal, setShowNouvelleConsModal] = useState(false);
  const [consPatientSearch, setConsPatientSearch] = useState('');
  const [consSearchResults, setConsSearchResults] = useState<any[]>([]);
  const [consSearching, setConsSearching] = useState(false);
  const consSearchTimer = useRef<NodeJS.Timeout | null>(null);
  const [consStartLoading, setConsStartLoading] = useState(false);
  const [consError, setConsError] = useState<string | null>(null);

  const todayStr = new Date().toISOString().split('T')[0];

  // ─── Auth + Chargement initial ──────────────────────────────────
  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login', { replace: true }); return; }
    if (role !== 'medecin' && role !== 'super_admin') { navigate('/', { replace: true }); return; }

    const loadMedecinInfo = async () => {
      try {
        setLoading(true);
        if (role === 'super_admin') {
          setIsSuperAdmin(true);
          await loadAllAppointments();
        } else {
          const { data: medecinData, error } = await supabase
            .from('medecins').select('*').eq('user_id', user.id).maybeSingle();
          if (error) throw error;
          if (medecinData) {
            setMedecinId(medecinData.id);
            setMedecinSpecialite(medecinData.specialite || 'Médecine générale');
            // Déterminer la salle : OLYMPIO Camille = chef, les autres = garde
            const isChef = medecinData.nom?.toUpperCase() === 'OLYMPIO' && medecinData.prenom?.toUpperCase() === 'CAMILLE';
            setFileAttenteSalle(isChef ? 'chef' : 'garde');
            await loadAppointments(medecinData.id);
          }
        }
      } catch (e) { console.error('Error loading info:', e); }
      finally { setLoading(false); }
    };
    loadMedecinInfo();

    const channel = supabase.channel('medecin_rdv_changes').on('postgres_changes', { event: '*', schema: 'public', table: 'rendez_vous' }, () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(async () => {
        setIsRefreshing(true);
        try {
          if (role === 'super_admin') await loadAllAppointments();
          else if (medecinId) await loadAppointments(medecinId);
          if (selectedDay) await loadDayDetail(selectedDay);
          clearOptimisticCache();
        } finally { setIsRefreshing(false); }
      }, 500);
    }).subscribe();

    return () => { if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current); supabase.removeChannel(channel); };
  }, [user, role, authLoading, navigate, medecinId]);

  // ─── Liste des patients de la clinique ──────────────────────
  useEffect(() => {
    if (!user) return;
    const loadClinicPatients = async () => {
      setPatientsLoading(true);
      try {
        const { data, error } = await supabase
          .from('patients')
          .select('id, nom, prenom, date_naissance, telephone, numero_dossier')
          .order('nom', { ascending: true })
          .limit(1000);
        if (error) throw error;
        setClinicPatients(data || []);
      } catch (e) {
        console.error('Erreur chargement patients:', e);
      } finally {
        setPatientsLoading(false);
      }
    };
    loadClinicPatients();
  }, [user]);

  // ─── Chargement RDV ─────────────────────────────────────────────
  const loadAppointments = async (medId: string) => {
    try {
      const { data, error } = await supabase.from('rendez_vous').select(`*, patients(id,nom,prenom,email,telephone,date_naissance,adresse,numero_dossier)`).eq('medecin_id', medId).order('date_rdv',{ascending:true}).order('heure_debut',{ascending:true});
      if (error) throw error;
      if (data) {
        const nowDate = new Date();
        const ids: string[] = [];
        data.forEach((rdv: any) => {
          const dt = new Date(`${rdv.date_rdv}T${rdv.heure_debut || '00:00'}:00`);
          if (dt < nowDate && (rdv.statut === 'en_attente' || rdv.statut === 'confirme')) { ids.push(rdv.id); rdv.statut = 'manque'; }
        });
        if (ids.length > 0) {
          await supabase.from('rendez_vous').update({ statut: 'manque' }).in('id', ids);
        }
        setAppointments(data);
      }
    } catch (e) { console.error('Error loading appointments:', e); }
  };

  const loadAllAppointments = async () => {
    try {
      const { data, error } = await supabase.from('rendez_vous').select(`*, patients!inner(id,nom,prenom,telephone,email,adresse,date_naissance,numero_dossier), medecins!inner(id,nom,prenom,specialite)`).order('date_rdv',{ascending:true}).order('heure_debut',{ascending:true});
      if (error) throw error;
      if (data) {
        const nowDate = new Date();
        const ids: string[] = [];
        data.forEach((rdv: any) => {
          const dt = new Date(`${rdv.date_rdv}T${rdv.heure_debut || '00:00'}:00`);
          if (dt < nowDate && (rdv.statut === 'en_attente' || rdv.statut === 'confirme')) { ids.push(rdv.id); rdv.statut = 'manque'; }
        });
        if (ids.length > 0) {
          await supabase.from('rendez_vous').update({ statut: 'manque' }).in('id', ids);
        }
        setAppointments(data);
      }
    } catch (e) { console.error('Error loading all appointments:', e); }
  };

  // ─── Détail d'un jour ───────────────────────────────────────────
  const loadDayDetail = async (dateStr: string) => {
    setLoadingDayDetail(true);
    const clickedDate = new Date(dateStr + 'T00:00:00');
    const todayDate = new Date(todayStr + 'T00:00:00');

    if (clickedDate < todayDate) {
      // Tous les médecins voient toutes les consultations du jour (continuité des soins)
      const query = supabase.from('consultations').select(`*, patients!inner(id,nom,prenom,numero_dossier,telephone,date_naissance), medecins(id,nom,prenom,specialite)`).eq('date_consultation', dateStr).order('heure_debut', { ascending: true });
      const { data } = await query;
      setDayConsultations(data || []);
    } else {
      setDayConsultations([]);
    }
    setLoadingDayDetail(false);
  };

  const handleDayClick = (dateStr: string) => {
    if (!dateStr) return;
    setSelectedDay(dateStr);
    loadDayDetail(dateStr);
  };

  // ─── Actions RDV ─────────────────────────────────────────────────
  const handleStartConsultationClick = (rdv: any) => { setSelectedRendezVous(rdv); setShowConfirmStartModal(true); };

  const startConsultation = async () => {
    if (!selectedRendezVous) return;
    try {
      const nowDate = new Date();
      await offlineMutate({
        table: 'rendez_vous',
        operation: 'UPDATE',
        recordId: selectedRendezVous.id,
        data: { statut: 'en_cours', heure_debut_reel: nowDate.toTimeString().slice(0, 8) },
        metadata: { summary: `Démarrage consultation — ${selectedRendezVous.patients?.nom || 'Patient'}` },
        onlineExecutor: async () => {
          await supabase.from('rendez_vous').update({ statut: 'en_cours', heure_debut_reel: nowDate.toTimeString().slice(0, 8) }).eq('id', selectedRendezVous.id);
          return { id: selectedRendezVous.id };
        },
      });
      setShowConfirmStartModal(false);
      const pid = selectedRendezVous.patients?.id;
      const rid = selectedRendezVous.id;
      setSelectedRendezVous(null);
      navigate(`/medecin/consultation?patientId=${pid}&rdvId=${rid}`);
    } catch (e) { console.error('Error starting consultation:', e); alert('Erreur lors du démarrage'); }
  };

  const openPatientHistory = (patientData: any) => {
    if (!patientData) return;
    setHistoryPatientId(patientData.id);
    setHistoryPatientName(`${patientData.prenom || ''} ${patientData.nom || ''}`);
    setShowHistoryModal(true);
  };

  const cancelAppointment = async (rdvId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir annuler ce rendez-vous ?')) return;
    try {
      await offlineMutate({
        table: 'rendez_vous',
        operation: 'UPDATE',
        recordId: rdvId,
        data: { statut: 'annule' },
        metadata: { summary: 'Annulation RDV' },
        onlineExecutor: async () => {
          await supabase.from('rendez_vous').update({ statut: 'annule' }).eq('id', rdvId);
          return { id: rdvId };
        },
      });
      if (isSuperAdmin) await loadAllAppointments();
      else if (medecinId) await loadAppointments(medecinId);
      if (selectedDay) loadDayDetail(selectedDay);
      clearOptimisticCache();
    } catch (e) { console.error('Error canceling:', e); alert('Erreur lors de l\'annulation'); }
  };

  // ─── Création RDV depuis l'agenda ────────────────────────────────
  const openCreateRdvForDate = (dateStr: string) => {
    setCreateRdvDate(dateStr);
    setCreateRdvHeureDebut('');
    setCreateRdvHeureFin('');
    setCreateRdvMotif('');
    setCreateRdvType('cabinet');
    setCreateRdvDossier('');
    setCreateRdvPatient(null);
    setCreateRdvNotFound(false);
    setShowCreateRdvModal(true);
  };

  useEffect(() => {
    if (createRdvSearchTimer.current) clearTimeout(createRdvSearchTimer.current);
    const dossier = createRdvDossier.trim().toUpperCase();
    if (!dossier) { setCreateRdvPatient(null); setCreateRdvNotFound(false); setCreateRdvSearching(false); return; }
    setCreateRdvSearching(true);
    setCreateRdvNotFound(false);
    createRdvSearchTimer.current = setTimeout(async () => {
      const { data } = await supabase.from('patients').select('id,nom,prenom,date_naissance,telephone,email,numero_dossier').ilike('numero_dossier', dossier).maybeSingle();
      if (data) { setCreateRdvPatient(data); setCreateRdvNotFound(false); }
      else { setCreateRdvPatient(null); if (dossier.length >= 2) setCreateRdvNotFound(true); }
      setCreateRdvSearching(false);
    }, 500);
  }, [createRdvDossier]);

  // ─── Recherche patient pour nouvelle consultation ─────────────
  useEffect(() => {
    if (consSearchTimer.current) clearTimeout(consSearchTimer.current);
    const term = consPatientSearch.trim();
    if (term.length < 2) { setConsSearchResults([]); setConsSearching(false); return; }
    setConsSearching(true);
    consSearchTimer.current = setTimeout(async () => {
      const { data } = await supabase
        .from('patients')
        .select('id,nom,prenom,date_naissance,telephone,numero_dossier')
        .or(`nom.ilike.%${term}%,prenom.ilike.%${term}%,numero_dossier.ilike.%${term}%`)
        .limit(10);
      setConsSearchResults(data || []);
      setConsSearching(false);
    }, 400);
  }, [consPatientSearch]);

  const handleStartNewConsultation = async (patient: any) => {
    if (!medecinId && !isSuperAdmin) return;
    setConsStartLoading(true);
    setConsError(null);
    try {
      const targetMedecinId = medecinId || (await supabase.from('medecins').select('id').limit(1).single()).data?.id;
      if (!targetMedecinId) { setConsError('Aucun médecin associé.'); return; }

      // Vérifier chevauchement : une consultation en cours existe déjà ?
      const { data: enCours } = await supabase
        .from('consultations')
        .select('id, patient_id')
        .eq('medecin_id', targetMedecinId)
        .in('statut', ['en_cours', 'partiellement_termine'])
        .neq('patient_id', patient.id)
        .limit(1)
        .maybeSingle();

      if (enCours) {
        const { data: p } = await supabase.from('patients').select('prenom,nom').eq('id', enCours.patient_id).single();
        const nomPatientEnCours = p ? `${p.prenom} ${p.nom}` : 'Patient inconnu';
        setConsError(`Vous avez déjà une consultation en cours avec ${nomPatientEnCours}. Veuillez la terminer avant d'en démarrer une nouvelle.`);
        return;
      }

      // Vérifier si une consultation existe déjà pour ce patient+medecin aujourd'hui
      const today = new Date().toISOString().split('T')[0];
      const { data: existingToday } = await supabase
        .from('consultations')
        .select('id, rendez_vous_id')
        .eq('medecin_id', targetMedecinId)
        .eq('patient_id', patient.id)
        .eq('date_consultation', today)
        .in('statut', ['en_cours', 'partiellement_termine'])
        .maybeSingle();

      if (existingToday) {
        setShowNouvelleConsModal(false);
        const navRdvId = existingToday.rendez_vous_id;
        if (navRdvId) {
          navigate(`/medecin/consultation?patientId=${patient.id}&rdvId=${navRdvId}`);
        } else {
          navigate(`/medecin/consultation?patientId=${patient.id}`);
        }
        return;
      }

      // Créer un rendez-vous "shadow" pour cette consultation directe
      const now = new Date();
      const heureDebut = now.toTimeString().slice(0, 8);
      const heureFin = new Date(now.getTime() + 30 * 60000).toTimeString().slice(0, 8);

      const { data: newRdv, error: rdvError } = await supabase
        .from('rendez_vous')
        .insert({
          medecin_id: targetMedecinId,
          patient_id: patient.id,
          date_rdv: today,
          heure_debut: heureDebut,
          heure_fin: heureFin,
          motif: 'Consultation directe',
          type_consultation: 'cabinet',
          statut: 'en_cours',
        })
        .select()
        .single();

      if (rdvError) throw rdvError;

      // Créer la consultation liée à ce rendez-vous
      const { data: newCons, error } = await supabase
        .from('consultations')
        .insert({
          patient_id: patient.id,
          medecin_id: targetMedecinId,
          rendez_vous_id: newRdv?.id || null,
          date_consultation: today,
          heure_debut: heureDebut,
          statut: 'en_cours',
          donnees_supplementaires: {} as any,
        })
        .select()
        .single();

      if (error) {
        if (error.message?.includes('déjà une consultation en cours') || error.code === 'P0001') {
          setConsError('Une consultation est déjà en cours pour ce médecin. Veuillez la terminer avant.');
          return;
        }
        throw error;
      }

      setShowNouvelleConsModal(false);
      navigate(`/medecin/consultation?patientId=${patient.id}&rdvId=${newRdv?.id}`);
    } catch (e: any) {
      console.error('Erreur nouvelle consultation:', e);
      setConsError(e.message || 'Erreur lors du démarrage de la consultation.');
    } finally {
      setConsStartLoading(false);
    }
  };

  const saveNewRendezVous = async () => {
    if (!createRdvPatient || !createRdvDate || !createRdvHeureDebut || !createRdvHeureFin || !createRdvMotif) { alert('Veuillez remplir tous les champs obligatoires'); return; }
    const dt = new Date(`${createRdvDate}T${createRdvHeureDebut}`);
    if (dt <= new Date()) { alert('Impossible de créer un rendez-vous dans le passé.'); return; }
    try {
      setCreateRdvSaving(true);
      let targetMedecinId = medecinId;
      if (!targetMedecinId) {
        const { data: firstMed } = await supabase.from('medecins').select('id').limit(1).single();
        targetMedecinId = firstMed?.id;
      }
      if (!targetMedecinId) { alert('Aucun médecin associé.'); setCreateRdvSaving(false); return; }

      await offlineMutate({
        table: 'rendez_vous',
        operation: 'INSERT',
        data: {
          medecin_id: targetMedecinId,
          patient_id: createRdvPatient.id,
          date_rdv: createRdvDate,
          heure_debut: createRdvHeureDebut,
          heure_fin: createRdvHeureFin,
          motif: createRdvMotif,
          type_consultation: createRdvType,
          statut: 'en_attente',
        },
        metadata: { summary: `Nouveau RDV — ${createRdvPatient.prenom} ${createRdvPatient.nom}` },
        onlineExecutor: async () => {
          const { data: existing } = await supabase.from('rendez_vous').select('id').eq('medecin_id', targetMedecinId).eq('date_rdv', createRdvDate).eq('heure_debut', createRdvHeureDebut).maybeSingle();
          if (existing) throw new Error('Un rendez-vous existe déjà à ce créneau.');
          const { data } = await supabase.from('rendez_vous').insert({
            medecin_id: targetMedecinId,
            patient_id: createRdvPatient.id,
            date_rdv: createRdvDate,
            heure_debut: createRdvHeureDebut,
            heure_fin: createRdvHeureFin,
            motif: createRdvMotif,
            type_consultation: createRdvType,
            statut: 'en_attente',
          }).select().single();
          return data;
        },
      });

      setShowCreateRdvModal(false);
      try {
        if (isSuperAdmin) await loadAllAppointments();
        else if (medecinId) await loadAppointments(medecinId);
        if (selectedDay) loadDayDetail(selectedDay);
        clearOptimisticCache();
      } catch (_) { /* rafraîchissement différé si offline */ }
    } catch (e: any) {
      if (e.message === 'Un rendez-vous existe déjà à ce créneau.') {
        alert(e.message);
      } else {
        console.error('Error creating RDV:', e);
        alert('Erreur lors de la création');
      }
    }
    finally { setCreateRdvSaving(false); }
  };

  // ─── Filtres ────────────────────────────────────────────────────
  const filterAppointments = (appts: any[]) => {
    if (!searchQuery.trim()) return appts;
    const q = searchQuery.toLowerCase().trim();
    return appts.filter(rdv => {
      const p = rdv.patients; if (!p) return false;
      const nom = (p.nom||'').toLowerCase(), prenom = (p.prenom||'').toLowerCase();
      return nom.includes(q) || prenom.includes(q) || `${prenom} ${nom}`.includes(q) || `${nom} ${prenom}`.includes(q) || (p.numero_dossier||'').toLowerCase().includes(q);
    });
  };

  // ─── Données ────────────────────────────────────────────────────
  const allUpcoming = appointments.filter(r => ['en_attente','confirme','en_cours'].includes(r.statut));
  const allPast = appointments.filter(r => ['termine','annule','manque'].includes(r.statut));
  const upcomingAppointments = filterAppointments(allUpcoming);
  const pastAppointments = filterAppointments(allPast);

  const filteredClinicPatients = patientSearchTerm.trim()
    ? clinicPatients.filter((p) => {
        const q = normaliserTexte(patientSearchTerm);
        const nom = normaliserTexte(p.nom || '');
        const prenom = normaliserTexte(p.prenom || '');
        const full = normaliserTexte(`${p.prenom || ''} ${p.nom || ''}`);
        const fullInv = normaliserTexte(`${p.nom || ''} ${p.prenom || ''}`);
        const dossier = normaliserTexte(p.numero_dossier || '');
        return nom.includes(q) || prenom.includes(q) || full.includes(q) || fullInv.includes(q) || dossier.includes(q);
      })
    : clinicPatients;
  const cells = getCalendarCells(calendarYear, calendarMonth);
  const getCountForDay = (dateStr: string) => appointments.filter(r => r.date_rdv === dateStr).length;
  const formatFullDate = (ds: string) => new Date(ds+'T00:00:00').toLocaleDateString('fr-FR',{weekday:'long',year:'numeric',month:'long',day:'numeric'});

  // ─── Rendu Calendrier (version compacte dans le panneau) ────────
  const renderCalendarContent = () => (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-xl">
      {/* En-tête mois */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
        <button onClick={() => { if (calendarMonth===0){setCalendarMonth(11);setCalendarYear(y=>y-1);}else setCalendarMonth(m=>m-1); setSelectedDay(null); }} className="w-9 h-9 rounded-lg hover:bg-gray-100 flex items-center justify-center cursor-pointer transition-colors">
          <i className="ri-arrow-left-s-line text-lg text-gray-600"></i>
        </button>
        <h2 className="text-base font-bold text-gray-900">{MONTHS[calendarMonth]} {calendarYear}</h2>
        <button onClick={() => { if (calendarMonth===11){setCalendarMonth(0);setCalendarYear(y=>y+1);}else setCalendarMonth(m=>m+1); setSelectedDay(null); }} className="w-9 h-9 rounded-lg hover:bg-gray-100 flex items-center justify-center cursor-pointer transition-colors">
          <i className="ri-arrow-right-s-line text-lg text-gray-600"></i>
        </button>
      </div>

      {/* Noms des jours */}
      <div className="grid grid-cols-7 px-3 py-1.5 bg-gray-50/50">
        {DAY_NAMES.map(d => <div key={d} className="text-center text-[11px] font-bold text-gray-500 py-1.5">{d}</div>)}
      </div>

      {/* Grille */}
      <div className="grid grid-cols-7 px-3 pb-3 gap-0.5">
        {cells.map((cell, idx) => {
          if (cell.day === 0) return <div key={`empty-${idx}`} className="aspect-square" />;
          const count = getCountForDay(cell.dateStr);
          const isToday = cell.dateStr === todayStr;
          const isPast = cell.dateStr < todayStr;
          const isSel = cell.dateStr === selectedDay;
          let bg = 'hover:bg-gray-100';
          if (isSel) bg = 'bg-clinic-blue text-white hover:bg-clinic-blue-dark';
          else if (isToday) bg = 'bg-clinic-blue/5 ring-2 ring-clinic-blue/40';
          else if (isPast) bg = 'bg-gray-50/80 text-gray-500 hover:bg-gray-100';

          return (
            <button
              key={cell.dateStr}
              onClick={() => { handleDayClick(cell.dateStr); }}
              className={`aspect-square rounded-lg flex flex-col items-center justify-center relative cursor-pointer transition-all ${bg}`}
            >
              <span className={`text-xs font-semibold ${isSel ? 'text-white' : isToday ? 'text-clinic-blue-dark' : ''}`}>{cell.day}</span>
              {count > 0 && (
                <span className={`text-[9px] font-bold mt-0.5 px-1 py-0.5 rounded-full leading-none ${isSel ? 'bg-white/30 text-white' : isPast ? 'bg-gray-300 text-gray-600' : 'bg-clinic-blue/10 text-clinic-blue-dark'}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Bouton Nouveau RDV */}
      <div className="px-5 pb-4 pt-1 flex justify-end">
        <button onClick={() => { const d = todayStr; setCreateRdvDate(d); setShowCreateRdvModal(true); }} className="px-4 py-2 bg-clinic-blue text-white rounded-full hover:bg-clinic-blue-dark transition-all font-semibold text-xs whitespace-nowrap cursor-pointer flex items-center gap-1.5">
          <i className="ri-add-line text-sm w-3 h-3 flex items-center justify-center"></i>
          Nouveau RDV
        </button>
      </div>
    </div>
  );

  // ─── Rendu détail jour dans le panneau ──────────────────────────
  const renderDayDetailInPanel = () => {
    const isPast = selectedDay! < todayStr;
    const dayRdvs = appointments.filter(r => r.date_rdv === selectedDay);

    return (
      <div className="mt-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
            <i className={`${isPast ? 'ri-history-line' : 'ri-calendar-event-line'} text-clinic-blue w-4 h-4 flex items-center justify-center`}></i>
            <span className="text-sm">{selectedDay && formatFullDate(selectedDay)}</span>
          </h3>
          <button onClick={() => { setSelectedDay(null); setDayConsultations([]); }} className="text-xs text-gray-500 hover:text-gray-700 cursor-pointer flex items-center gap-1">
            <i className="ri-close-line w-3 h-3 flex items-center justify-center"></i> Fermer
          </button>
        </div>

        {loadingDayDetail ? (
          <div className="text-center py-8"><i className="ri-loader-4-line text-2xl text-clinic-blue animate-spin mb-2"></i><p className="text-sm text-gray-500">Chargement...</p></div>
        ) : isPast ? (
          dayConsultations.length === 0 ? (
            <div className="text-center py-8">
              <i className="ri-file-history-line text-4xl text-gray-300 mb-3"></i>
              <p className="text-sm text-gray-500">Aucune consultation ce jour-là</p>
              {dayRdvs.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-semibold text-gray-600 mb-2">{dayRdvs.length} rendez-vous prévus :</p>
                  <div className="space-y-1.5">
                    {dayRdvs.map((rdv: any) => (
                      <div key={rdv.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
                        <button onClick={() => openPatientHistory(rdv.patients)} className="text-xs font-semibold text-gray-800 hover:text-clinic-blue hover:underline cursor-pointer text-left">
                          {rdv.patients?.nom} {rdv.patients?.prenom}
                        </button>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${rdv.statut==='manque'?'bg-red-100 text-red-700':rdv.statut==='annule'?'bg-gray-100 text-gray-600':'bg-green-100 text-green-700'}`}>
                          {rdv.statut==='manque'?'Manqué':rdv.statut==='annule'?'Annulé':'Terminé'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {dayConsultations.map((cons: any) => {
                const p = cons.patients;
                const m = cons.medecins;
                return (
                  <div key={cons.id} className="bg-gray-50 rounded-xl border border-gray-200 p-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <button onClick={() => p && openPatientHistory(p)} className="text-sm font-bold text-gray-900 hover:text-clinic-blue hover:underline cursor-pointer text-left">
                          {p?.nom} {p?.prenom}
                        </button>
                        <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500 flex-wrap">
                          <span>{p?.numero_dossier || 'N/A'}</span>
                          {m && <span>Dr {m.nom} {m.prenom}</span>}
                        </div>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold shrink-0 ${cons.statut==='termine'?'bg-green-100 text-green-700':cons.statut==='partiellement_termine'?'bg-amber-100 text-amber-700':'bg-blue-100 text-blue-700'}`}>
                        {cons.statut==='termine'?'Terminé':cons.statut==='partiellement_termine'?'Pause labo':'En cours'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          dayRdvs.length === 0 ? (
            <div className="text-center py-8">
              <i className="ri-calendar-line text-4xl text-gray-300 mb-3"></i>
              <p className="text-sm text-gray-500 mb-3">Aucun rendez-vous ce jour-là</p>
              <button onClick={() => openCreateRdvForDate(selectedDay!)} className="px-4 py-2 bg-clinic-blue text-white rounded-full hover:bg-clinic-blue-dark transition-all font-semibold text-xs whitespace-nowrap cursor-pointer">
                <i className="ri-add-line mr-1"></i>Ajouter
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {dayRdvs.map((rdv: any) => (
                <div key={rdv.id} className="bg-gray-50 rounded-xl border border-gray-200 p-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <button onClick={() => openPatientHistory(rdv.patients)} className="text-sm font-bold text-gray-900 hover:text-clinic-blue hover:underline cursor-pointer text-left">
                        {rdv.patients?.nom} {rdv.patients?.prenom}
                      </button>
                      <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500 flex-wrap">
                        <span>{rdv.heure_debut?.substring(0,5)} - {rdv.heure_fin?.substring(0,5)}</span>
                        {isSuperAdmin && rdv.medecins && <span>Dr {rdv.medecins.nom}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${rdv.statut==='en_cours'?'bg-orange-100 text-orange-700':rdv.statut==='confirme'?'bg-green-100 text-green-700':'bg-blue-100 text-blue-700'}`}>
                        {rdv.statut==='en_cours'?'En cours':rdv.statut==='confirme'?'Confirmé':'En attente'}
                      </span>
                      {!isSuperAdmin && rdv.statut==='en_attente' && (
                        <button onClick={() => handleStartConsultationClick(rdv)} className="px-2 py-0.5 bg-clinic-blue text-white rounded-full hover:bg-clinic-blue-dark text-[10px] font-medium whitespace-nowrap cursor-pointer">
                          Démarrer
                        </button>
                      )}
                      {!isSuperAdmin && rdv.statut==='en_cours' && (
                        <button onClick={() => navigate(`/medecin/consultation?patientId=${rdv.patients?.id}&rdvId=${rdv.id}`)} className="px-2 py-0.5 bg-amber-500 text-white rounded-full hover:bg-amber-600 text-[10px] font-medium whitespace-nowrap cursor-pointer">
                          Reprendre
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    );
  };

  // ─── Rendu principal ─────────────────────────────────────────────
  if (authLoading || loading) {
    return <div className="min-h-screen bg-white flex items-center justify-center"><div className="text-center"><i className="ri-loader-4-line text-4xl text-clinic-blue animate-spin mb-4"></i><div className="text-gray-700 text-xl">Chargement...</div></div></div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* ─── En-tête + bouton Agenda ───────────────────────── */}
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-3">
            <div>
              <h1 className="text-xl md:text-3xl font-bold text-gray-900 mb-1">{isSuperAdmin ? 'Tous les Rendez-vous' : 'Mon Agenda'}</h1>
              <p className="text-gray-600 text-xs md:text-sm">{isSuperAdmin ? 'Vue d\'ensemble de tous les rendez-vous de la clinique' : `Gérez vos consultations - ${medecinSpecialite}`}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                id="guide-btn-nouvelle-consultation"
                onClick={() => setShowNouvelleConsModal(true)}
                className="px-3 md:px-4 py-2 md:py-2.5 rounded-full font-semibold text-xs md:text-sm whitespace-nowrap cursor-pointer transition-all flex items-center gap-1.5 bg-clinic-blue text-white border-2 border-clinic-blue hover:bg-clinic-blue-dark hover:border-clinic-blue-dark shadow-sm"
              >
                                Nouvelle consultation
              </button>
              <button
                id="guide-btn-file-attente"
                onClick={() => navigate(`/file-attente?vue=medecin&salle=${fileAttenteSalle}`)}
                className="px-3 md:px-4 py-2 md:py-2.5 rounded-full font-semibold text-xs md:text-sm whitespace-nowrap cursor-pointer transition-all flex items-center gap-1.5 bg-white text-clinic-blue-dark border-2 border-clinic-blue/30 hover:bg-clinic-blue/5 hover:border-clinic-blue"
              >
                                File d'attente
              </button>
              <button
                id="guide-btn-agenda"
                onClick={() => { setShowCalendar(!showCalendar); if (!showCalendar) { setSelectedDay(null); setDayConsultations([]); } }}
                className={`px-3 md:px-4 py-2 md:py-2.5 rounded-full font-semibold text-xs md:text-sm whitespace-nowrap cursor-pointer transition-all flex items-center gap-1.5 ${showCalendar ? 'bg-clinic-blue text-white shadow-lg' : 'bg-white text-clinic-blue-dark border-2 border-clinic-blue/30 hover:bg-clinic-blue/5 hover:border-clinic-blue'}`}
              >
                                {showCalendar ? 'Fermer' : 'Agenda'}
                {!showCalendar && getCountForDay(todayStr) > 0 && (
                  <span className="bg-clinic-blue text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none ml-0.5">{getCountForDay(todayStr)}</span>
                )}
              </button>
              <button
                onClick={() => navigate('/medecin/guide')}
                className="px-3 md:px-4 py-2 md:py-2.5 rounded-full font-semibold text-xs md:text-sm whitespace-nowrap cursor-pointer transition-all flex items-center gap-1.5 bg-white text-clinic-blue-dark border-2 border-clinic-blue/20 hover:bg-clinic-blue/5 hover:border-clinic-blue"
              >
                                Guide
              </button>
            </div>
          </div>
        </div>

        {/* ─── Panneau Calendrier (affiché au clic) ──────────── */}
        {showCalendar && (
          <div className="mb-8 animate-[fadeIn_0.2s_ease-out]">
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="lg:w-[380px] shrink-0">
                {renderCalendarContent()}
              </div>
              {/* Détail du jour dans le panneau */}
              {selectedDay && (
                <div className="flex-1 bg-white rounded-2xl border border-gray-200 p-5 min-h-[200px]">
                  {renderDayDetailInPanel()}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── Barre de recherche ────────────────────────────── */}
        <div className="mb-6">
          <div className="relative max-w-xl">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <i className="ri-search-line text-gray-400 text-lg w-4 h-4 flex items-center justify-center"></i>
            </div>
            <input type="text" placeholder="Rechercher un patient (nom, prénom, n° dossier)..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-10 py-3 bg-white border-2 border-gray-200 rounded-2xl text-sm text-gray-700 placeholder-gray-400 focus:border-clinic-blue/30 focus:outline-none transition-colors" />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer">
                <i className="ri-close-circle-fill text-gray-400 hover:text-gray-600 text-lg w-4 h-4 flex items-center justify-center"></i>
              </button>
            )}
          </div>
        </div>

        {/* ─── Rendez-vous à venir ───────────────────────────── */}
        <div className="mb-12" id="guide-list-rdv">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
            <h2 className="text-lg md:text-2xl font-bold text-gray-900 flex items-center min-w-0">
                            <span className="truncate">Rendez-vous à venir</span>
            </h2>
            <div className="flex items-center gap-3 shrink-0">
              {isRefreshing && <span className="text-xs text-gray-400 flex items-center gap-1"><i className="ri-loader-4-line animate-spin w-3 h-3 flex items-center justify-center"></i>Actualisation...</span>}
              <div className="px-3 md:px-4 py-1.5 md:py-2 bg-clinic-blue/10 text-clinic-blue-dark rounded-full font-semibold text-xs md:text-sm whitespace-nowrap">{upcomingAppointments.length} rendez-vous</div>
            </div>
          </div>
          {allUpcoming.length===0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center"><i className="ri-calendar-line text-6xl text-gray-300 mb-4"></i><p className="text-gray-500 text-lg">Aucun rendez-vous à venir</p></div>
          ) : upcomingAppointments.length===0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center"><i className="ri-search-line text-4xl text-gray-300 mb-3"></i><p className="text-gray-500">Aucun résultat pour "{searchQuery}"</p></div>
          ) : (
            <div className="space-y-4">
              {upcomingAppointments.map((rdv: any) => (
                <div key={rdv.id} className="bg-white rounded-2xl border border-gray-200 hover:border-clinic-blue/30 hover:shadow-sm transition-all p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center mb-3">
                        <div>
                          <button onClick={() => openPatientHistory(rdv.patients)} className="text-xl font-bold text-gray-900 hover:text-clinic-blue hover:underline transition-colors cursor-pointer text-left">
                            {rdv.patients?.nom} {rdv.patients?.prenom}
                          </button>
                          {isSuperAdmin && rdv.medecins && <p className="text-sm text-gray-600">Dr {rdv.medecins.nom} {rdv.medecins.prenom} - {rdv.medecins.specialite}</p>}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 md:gap-4 mb-3 md:mb-4">
                        <div className="flex items-center text-gray-700"><span className="font-medium text-sm">{new Date(rdv.date_rdv).toLocaleDateString('fr-FR',{weekday:'long',year:'numeric',month:'long',day:'numeric'})}</span></div>
                        <div className="flex items-center text-gray-700"><span className="font-medium text-sm">{rdv.heure_debut?.substring(0,5)} - {rdv.heure_fin?.substring(0,5)}</span></div>
                        <div className="flex items-center text-gray-700"><span className="font-medium text-sm">{rdv.patients?.telephone}</span></div>
                      </div>
                      {rdv.motif && <div className="bg-clinic-blue/5 p-3 rounded-xl mb-4"><div className="font-semibold text-clinic-blue-dark mb-1">Motif :</div><div className="text-gray-700 text-sm">{rdv.motif}</div></div>}
                      {rdv.heure_debut_reel && <div className="bg-green-50 p-3 rounded-xl mb-4"><div className="font-semibold text-green-800 mb-1"><i className="ri-time-line mr-2"></i>Consultation démarrée à : {rdv.heure_debut_reel.substring(0,5)}</div></div>}
                    </div>
                    <div className="ml-4 flex flex-col items-end gap-2">
                      <span className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap ${rdv.statut==='en_cours'?'bg-orange-100 text-orange-700':rdv.statut==='confirme'?'bg-green-100 text-green-700':'bg-blue-100 text-blue-700'}`}>
                        {rdv.statut==='en_cours'?'En cours':rdv.statut==='confirme'?'Confirmé':'En attente'}
                      </span>
                    </div>
                  </div>
                  {!isSuperAdmin && (
                    <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
                      {rdv.statut==='en_attente' && <button onClick={() => handleStartConsultationClick(rdv)} className="px-4 py-2 bg-clinic-blue text-white rounded-full hover:bg-clinic-blue-dark transition-all font-medium whitespace-nowrap cursor-pointer"><i className="ri-play-circle-line mr-2"></i>Démarrer</button>}
                      {rdv.statut==='en_cours' && <button onClick={() => navigate(`/medecin/consultation?patientId=${rdv.patients?.id}&rdvId=${rdv.id}`)} className="px-4 py-2 bg-amber-500 text-white rounded-full hover:bg-amber-600 transition-all font-medium whitespace-nowrap cursor-pointer"><i className="ri-play-circle-line mr-2"></i>Reprendre</button>}
                      {rdv.statut!=='en_cours' && <button onClick={() => cancelAppointment(rdv.id)} className="px-4 py-2 bg-red-100 text-red-700 rounded-full hover:bg-red-200 transition-all font-medium whitespace-nowrap cursor-pointer"><i className="ri-close-circle-line mr-2"></i>Annuler</button>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ─── Historique + Patients de la clinique ───────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          <div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
            <h2 className="text-lg md:text-2xl font-bold text-gray-900 flex items-center min-w-0"><span className="truncate">Historique</span></h2>
            <div className="px-3 md:px-4 py-1.5 md:py-2 bg-gray-100 text-gray-700 rounded-full font-semibold text-xs md:text-sm whitespace-nowrap shrink-0">{pastAppointments.length} rendez-vous</div>
          </div>
          {allPast.length===0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center"><i className="ri-history-line text-6xl text-gray-300 mb-4"></i><p className="text-gray-500 text-lg">Aucun historique</p></div>
          ) : pastAppointments.length===0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center"><i className="ri-search-line text-4xl text-gray-300 mb-3"></i><p className="text-gray-500">Aucun résultat pour "{searchQuery}"</p></div>
          ) : (
            <div className="space-y-2">
              {pastAppointments.map((rdv: any) => (
                <div key={rdv.id} className="bg-white rounded-2xl border border-gray-200 hover:border-clinic-blue/30 hover:shadow-sm transition-all p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="min-w-0">
                      <button onClick={() => openPatientHistory(rdv.patients)} className="text-base font-bold text-clinic-blue hover:text-clinic-blue-dark hover:underline transition-colors cursor-pointer text-left whitespace-nowrap">
                        {rdv.patients?.nom} {rdv.patients?.prenom}
                      </button>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500 flex-wrap">
                        <span>{new Date(rdv.date_rdv).toLocaleDateString('fr-FR',{weekday:'long',year:'numeric',month:'long',day:'numeric'})}</span>
                        <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                        <span>{rdv.heure_debut?.substring(0,5)} - {rdv.heure_fin?.substring(0,5)}</span>
                        {rdv.heure_debut_reel && rdv.heure_fin_reel && <><span className="w-1 h-1 bg-gray-300 rounded-full"></span><span>Réel : {rdv.heure_debut_reel.substring(0,5)} - {rdv.heure_fin_reel.substring(0,5)}</span></>}
                        {isSuperAdmin && rdv.medecins && <><span className="w-1 h-1 bg-gray-300 rounded-full"></span><span>Dr {rdv.medecins.nom} {rdv.medecins.prenom}</span></>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 ml-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${rdv.statut==='termine'?'bg-green-100 text-green-700':rdv.statut==='annule'?'bg-red-100 text-red-700':'bg-gray-100 text-gray-700'}`}>
                      {rdv.statut==='termine'?'Terminé':rdv.statut==='annule'?'Annulé':'Manqué'}
                    </span>
                    <i className="ri-arrow-right-s-line text-gray-300"></i>
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>

          {/* ─── Patients de la clinique ──────────────────── */}
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden flex flex-col" id="guide-patients-clinique">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                Patients de la clinique
              </h2>
              <div className="px-3 py-1 bg-clinic-blue/10 text-clinic-blue-dark rounded-full font-semibold text-xs whitespace-nowrap">{filteredClinicPatients.length} patient{filteredClinicPatients.length !== 1 ? 's' : ''}</div>
            </div>

            <div className="px-4 py-3 border-b border-gray-100">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-search-line text-gray-400 text-base w-4 h-4 flex items-center justify-center"></i>
                </div>
                <input
                  type="text"
                  value={patientSearchTerm}
                  onChange={(e) => setPatientSearchTerm(e.target.value)}
                  placeholder="Rechercher un patient (nom, prénom, dossier)..."
                  className="w-full pl-10 pr-9 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:border-clinic-blue/30 focus:bg-white focus:outline-none transition-colors"
                />
                {patientSearchTerm && (
                  <button onClick={() => setPatientSearchTerm('')} className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer">
                    <i className="ri-close-circle-fill text-gray-400 hover:text-gray-600 w-4 h-4 flex items-center justify-center"></i>
                  </button>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto max-h-[560px]">
              {patientsLoading ? (
                <div className="py-12 text-center">
                  <i className="ri-loader-4-line text-2xl text-clinic-blue animate-spin"></i>
                  <p className="text-sm text-gray-500 mt-2">Chargement des patients...</p>
                </div>
              ) : filteredClinicPatients.length === 0 ? (
                <div className="py-12 text-center">
                  <i className="ri-user-search-line text-4xl text-gray-300"></i>
                  <p className="text-sm text-gray-500 mt-2">Aucun patient trouvé</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {filteredClinicPatients.map((p: any) => (
                    <button
                      key={p.id}
                      onClick={() => openPatientHistory(p)}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-clinic-blue/5 transition-colors cursor-pointer"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-gray-900 truncate">{p.prenom} <span className="uppercase">{p.nom}</span></p>
                        <p className="text-xs text-gray-500 truncate">
                          {p.numero_dossier ? `Dossier ${p.numero_dossier}` : 'Dossier N/A'}
                          {p.telephone ? ` • ${p.telephone}` : ''}
                        </p>
                      </div>
                      <i className="ri-arrow-right-s-line text-gray-300 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ─── Modal Nouvelle Consultation ───────────────────── */}
      {showNouvelleConsModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => { setShowNouvelleConsModal(false); setConsError(null); }} />
          <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-3xl z-10">
              <h2 className="text-lg font-bold text-gray-900">Nouvelle consultation</h2>
              <button onClick={() => { setShowNouvelleConsModal(false); setConsError(null); }} className="w-10 h-10 rounded-xl hover:bg-gray-100 flex items-center justify-center cursor-pointer">
                <i className="ri-close-line text-xl text-gray-600"></i>
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Rechercher un patient</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <i className="ri-search-line text-gray-400 w-4 h-4 flex items-center justify-center"></i>
                  </div>
                  <input
                    type="text"
                    value={consPatientSearch}
                    onChange={(e) => setConsPatientSearch(e.target.value)}
                    placeholder="Nom, prénom ou n° de dossier..."
                    autoFocus
                    className="w-full pl-11 pr-10 py-3 border-2 border-gray-200 rounded-xl text-sm focus:border-clinic-blue/30 focus:outline-none transition-colors"
                  />
                  {consSearching && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <i className="ri-loader-4-line text-clinic-blue animate-spin w-4 h-4 flex items-center justify-center"></i>
                    </div>
                  )}
                  {consPatientSearch && !consSearching && (
                    <button
                      onClick={() => setConsPatientSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer"
                    >
                      <i className="ri-close-circle-fill text-gray-400 hover:text-gray-600 w-4 h-4 flex items-center justify-center"></i>
                    </button>
                  )}
                </div>
              </div>

              {consError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                  <i className="ri-error-warning-line text-red-500 w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                  <p className="text-sm text-red-700">{consError}</p>
                </div>
              )}

              <div className="space-y-2">
                {consSearchResults.length === 0 && consPatientSearch.trim().length >= 2 && !consSearching && (
                  <div className="text-center py-6">
                    <i className="ri-user-search-line text-3xl text-gray-300 mb-2"></i>
                    <p className="text-sm text-gray-500">Aucun patient trouvé</p>
                  </div>
                )}
                {consSearchResults.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => handleStartNewConsultation(p)}
                    disabled={consStartLoading}
                    className="w-full text-left bg-gray-50 hover:bg-clinic-blue/5 border border-gray-200 hover:border-clinic-blue/30 rounded-xl p-4 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-bold text-gray-900">{p.prenom} <span className="uppercase">{p.nom}</span></p>
                        <p className="text-xs text-gray-500">Dossier {p.numero_dossier || 'N/A'} • {p.telephone || 'N/A'}</p>
                      </div>
                      <i className="ri-arrow-right-s-line text-gray-300 w-5 h-5 flex items-center justify-center"></i>
                    </div>
                  </button>
                ))}
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => { setShowNouvelleConsModal(false); setConsError(null); }}
                  className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 font-medium whitespace-nowrap cursor-pointer transition-colors"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Modal Création RDV ─────────────────────────────── */}
      {showCreateRdvModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowCreateRdvModal(false)} />
          <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-3xl z-10">
              <h2 className="text-lg font-bold text-gray-900">Nouveau Rendez-vous</h2>
              <button onClick={() => setShowCreateRdvModal(false)} className="w-10 h-10 rounded-xl hover:bg-gray-100 flex items-center justify-center cursor-pointer">
                <i className="ri-close-line text-xl text-gray-600"></i>
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Date <span className="text-red-500">*</span></label>
                <input type="date" value={createRdvDate} onChange={e => setCreateRdvDate(e.target.value)} min={todayStr}
                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-clinic-blue/30 focus:outline-none text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Début <span className="text-red-500">*</span></label>
                  <input type="time" value={createRdvHeureDebut} onChange={e => setCreateRdvHeureDebut(e.target.value)}
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-clinic-blue/30 focus:outline-none text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Fin <span className="text-red-500">*</span></label>
                  <input type="time" value={createRdvHeureFin} onChange={e => setCreateRdvHeureFin(e.target.value)}
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-clinic-blue/30 focus:outline-none text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Type</label>
                <select value={createRdvType} onChange={e => setCreateRdvType(e.target.value)}
                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-clinic-blue/30 focus:outline-none text-sm cursor-pointer">
                  <option value="cabinet">Au cabinet</option>
                  <option value="domicile">À domicile</option>
                  <option value="teleconsultation">Téléconsultation</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Patient (n° dossier) <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input type="text" value={createRdvDossier} onChange={e => setCreateRdvDossier(e.target.value)} placeholder="Ex: CSF1393" autoComplete="off"
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-clinic-blue/30 focus:outline-none text-sm uppercase tracking-wider" />
                  {createRdvSearching && <div className="absolute right-3 top-1/2 -translate-y-1/2"><i className="ri-loader-4-line text-clinic-blue animate-spin"></i></div>}
                </div>
                {createRdvNotFound && !createRdvSearching && (
                  <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1"><i className="ri-error-warning-line w-3 h-3 flex items-center justify-center"></i>Aucun patient trouvé</p>
                )}
                {createRdvPatient && (
                  <div className="mt-2 bg-clinic-blue/5 border border-clinic-blue/20 rounded-xl p-3 flex items-center gap-3">
                    <i className="ri-user-heart-line text-clinic-blue w-4 h-4 flex items-center justify-center"></i>
                    <div>
                      <p className="text-sm font-bold text-clinic-blue-dark">{createRdvPatient.prenom} <span className="uppercase">{createRdvPatient.nom}</span></p>
                      <p className="text-xs text-clinic-blue">Dossier {createRdvPatient.numero_dossier}</p>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Motif <span className="text-red-500">*</span></label>
                <textarea value={createRdvMotif} onChange={e => setCreateRdvMotif(e.target.value)} rows={3} maxLength={500}
                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-clinic-blue/30 focus:outline-none resize-none text-sm" placeholder="Motif de la consultation..." />
                <p className="text-xs text-gray-500 mt-1">{createRdvMotif.length}/500</p>
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowCreateRdvModal(false)} className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 font-medium whitespace-nowrap cursor-pointer transition-colors">Annuler</button>
                <button onClick={saveNewRendezVous} disabled={createRdvSaving || !createRdvPatient}
                  className="flex-1 px-4 py-3 bg-clinic-blue text-white rounded-xl hover:bg-clinic-blue-dark font-semibold whitespace-nowrap cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  {createRdvSaving ? <><i className="ri-loader-4-line animate-spin mr-2"></i>Création...</> : <><i className="ri-calendar-check-line mr-2"></i>Créer le RDV</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals existants */}
      {showConfirmStartModal && selectedRendezVous && (
        <ConfirmStartConsultationModal isOpen={showConfirmStartModal} onClose={() => { setShowConfirmStartModal(false); setSelectedRendezVous(null); }} onConfirm={startConsultation} patientNom={`${selectedRendezVous.patients?.nom} ${selectedRendezVous.patients?.prenom}`} />
      )}
      <PatientHistoryModal isOpen={showHistoryModal} onClose={() => setShowHistoryModal(false)} patientId={historyPatientId} patientName={historyPatientName} />
      <MedecinTour />
      <Footer />
    </div>
  );
}